import { daysBetween } from '~/calculations/calendar'
import type { PhaseHistoryPatch, PhaseRecord } from '~/models/phase'
import { hasReachedPlannedEnd } from '~/models/phase'
import type { SolaDatabase } from '~/storage/db'
import { type StorageError, storageError } from '~/storage/errors'
import {
  acknowledgePlannedEnd,
  completePhase,
  correctPhaseHistory,
  getActivePhase,
  listPhases,
  updateActivePhase,
} from '~/storage/repositories/phaseRepository'
import { toCalendarDate } from '~/types/dates'
import type { PhaseId } from '~/types/ids'
import { err, ok, type Result } from '~/utils/result'
import { todayFor } from './weightService'

/**
 * Phase end and phase history (D-020, D-070).
 *
 * ────────────────────────────────────────────────────────────────────────────
 *  REACHING A PLANNED END WARNS. IT NEVER CHANGES ANYTHING.
 * ────────────────────────────────────────────────────────────────────────────
 *
 * Every function that changes a phase here is reached only from an explicit user
 * action. There is no scheduler, no "auto-complete on load", and no branch that
 * transitions a phase because a date passed — D-020 forbids it, and the absence
 * is the feature. The nearest thing to automation is `acknowledgePlannedEnd`,
 * which records that the user has *seen* the warning and changes nothing else.
 */

export interface PhaseEndState {
  readonly phase: PhaseRecord | undefined
  /** True once today is on or after the planned end date. */
  readonly reachedPlannedEnd: boolean
  /** Zero on the planned end date itself, then one per day after it. */
  readonly daysPastPlannedEnd: number
  /** The user has seen the warning. Not a decision, and not a transition. */
  readonly acknowledged: boolean
}

export async function getPhaseEndState(
  db: SolaDatabase,
  now: Date,
): Promise<Result<PhaseEndState, StorageError>> {
  const active = await getActivePhase(db)
  if (!active.ok) return active

  const phase = active.value
  if (phase === undefined) {
    return ok({
      phase: undefined,
      reachedPlannedEnd: false,
      daysPastPlannedEnd: 0,
      acknowledged: false,
    })
  }

  const today = todayFor(now)
  const reached = hasReachedPlannedEnd(phase, today)

  return ok({
    phase,
    reachedPlannedEnd: reached,
    daysPastPlannedEnd: reached ? daysBetween(phase.plannedEndDate, today) : 0,
    acknowledged: phase.plannedEndAcknowledged === true,
  })
}

/** Record that the warning has been seen. Changes nothing else (D-020). */
export async function acknowledgePhaseEnd(
  db: SolaDatabase,
  id: PhaseId,
  now: Date,
): Promise<Result<PhaseRecord, StorageError>> {
  return acknowledgePlannedEnd(db, id, now)
}

export interface EndPhaseRequest {
  readonly actualEndDate: string
  readonly transitionReason?: string
}

/**
 * End the active phase — one of the three explicit decisions D-020 requires.
 *
 * **It does not start anything.** Choosing what comes next is a separate
 * decision the user makes separately, and creating a successor here would be the
 * automatic transition D-020 exists to prevent. The app is left with no active
 * phase, and says so, which is honest rather than tidy.
 */
export async function endActivePhase(
  db: SolaDatabase,
  request: EndPhaseRequest,
  now: Date,
): Promise<Result<PhaseRecord, StorageError>> {
  const active = await getActivePhase(db)
  if (!active.ok) return active
  if (active.value === undefined) {
    return err(storageError('invalidRecord', 'There is no active phase to end.'))
  }

  const endDate = toCalendarDate(request.actualEndDate)
  if (endDate === undefined) {
    return err(storageError('invalidRecord', 'End date must be a real date in YYYY-MM-DD form.'))
  }

  return completePhase(db, active.value.id, endDate, now, request.transitionReason)
}

/**
 * Extend the active phase — the second explicit decision.
 *
 * Moving the planned end clears the acknowledgement, which the repository does:
 * having seen a warning about one date says nothing about a later one, and
 * leaving it set would suppress the warning for an end the user has never seen.
 */
export async function extendActivePhase(
  db: SolaDatabase,
  plannedEndDate: string,
  now: Date,
): Promise<Result<PhaseRecord, StorageError>> {
  const active = await getActivePhase(db)
  if (!active.ok) return active

  const phase = active.value
  if (phase === undefined) {
    return err(storageError('invalidRecord', 'There is no active phase to extend.'))
  }

  return updateActivePhase(
    db,
    phase.id,
    {
      type: phase.type,
      startDate: phase.startDate,
      plannedEndDate,
      targetWeeklyChangeMinPercent: phase.targetWeeklyChangeMinPercent,
      targetWeeklyChangeMaxPercent: phase.targetWeeklyChangeMaxPercent,
      ...(phase.notes === undefined ? {} : { notes: phase.notes }),
    },
    now,
  )
}

/**
 * Every phase, newest first.
 *
 * The repository returns them by start date ascending, which is right for the
 * index and wrong for a history list — the phase a user wants to correct is
 * almost always the most recent one.
 */
export async function listPhaseHistory(
  db: SolaDatabase,
): Promise<Result<readonly PhaseRecord[], StorageError>> {
  const phases = await listPhases(db)
  if (!phases.ok) return phases

  return ok(
    [...phases.value].sort((left, right) =>
      (right.startDate as string).localeCompare(left.startDate as string),
    ),
  )
}

/** Correct a phase's dates, notes or transition reason (D-070). */
export async function correctPhase(
  db: SolaDatabase,
  id: PhaseId,
  patch: PhaseHistoryPatch,
  now: Date,
): Promise<Result<PhaseRecord, StorageError>> {
  return correctPhaseHistory(db, id, patch, now)
}
